<template>
  <div class="gc-bom-bulk-update-material-config">
    <div class="config-card">
      <div class="config-card-header">
        <span class="config-card-title">Material Update Operation</span>
        <span class="config-card-title-secondary"
          >Based on material search criteria</span
        >
      </div>
      <div class="config-card-body">
        <span
          v-if="validated && !selectedOperation"
          class="param-error param-error-top"
        >
          Please select a material update operation.
        </span>

        <div class="operation-section">
          <div class="operation-section-header" v-if="selectedOperation">
            <DxButton
              text="Clear"
              type="default"
              hint="Clear selection"
              @click="selectedOperation = null"
              styling-mode="text"
              class="operation-clear-btn"
            />
          </div>

          <div :key="opListKey" class="operation-list">
            <div
              v-for="op in materialOperations"
              :key="op.value"
              :class="[
                'operation-item',
                { 'op-active': selectedOperation === op.value },
              ]"
            >
              <!-- Radio header row -->
              <label class="op-header">
                <input
                  type="radio"
                  :value="op.value"
                  :checked="selectedOperation === op.value"
                  :disabled="isReadOnly"
                  class="op-radio"
                  @change="onOperationChange(op.value)"
                />
                <div class="op-text">
                  <span class="op-label">{{ op.label }}</span>
                  <span class="op-desc">{{ op.description }}</span>
                </div>
              </label>

              <!-- Inline param form — expands when this op is selected -->
              <div
                v-if="selectedOperation === op.value"
                class="op-params-inline"
              >
                <!-- UpdateQtyAll -->
                <template v-if="op.value === 'UpdateQtyAll'">
                  <div v-if="quantityRange" class="qty-range-context">
                    <span class="qty-range-label"
                      >CURRENT QTY RANGE ACROSS MATCHED BOMS</span
                    >
                    <div class="qty-range-stats">
                      <div class="qty-stat-box">
                        <span class="qty-stat-key">Min</span>
                        <span class="qty-stat-val">
                          {{ getFormattedQty(quantityRange.min) }}
                          <span v-if="quantityRange.uom" class="qty-stat-uom">
                            {{ quantityRange.uom }}
                          </span>
                        </span>
                      </div>
                      <div class="qty-stat-box">
                        <span class="qty-stat-key">Max</span>
                        <span class="qty-stat-val">
                          {{ getFormattedQty(quantityRange.max) }}
                          <span v-if="quantityRange.uom" class="qty-stat-uom">
                            {{ quantityRange.uom }}
                          </span>
                        </span>
                      </div>
                      <div class="qty-stat-box">
                        <span class="qty-stat-key">Avg</span>
                        <span class="qty-stat-val">
                          {{ getFormattedQty(quantityRange.avg) }}
                          <span v-if="quantityRange.uom" class="qty-stat-uom">
                            {{ quantityRange.uom }}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div v-if="qtyAllRows.length > 0" class="replacement-header">
                    <span class="rt-col-code"
                      >Current Qty
                      <span class="qty-optional"
                        >(optional — exact match)</span
                      ></span
                    >
                    <span class="rt-col-qty"
                      >New Qty <span class="required-mark">*</span></span
                    >
                    <span class="rt-col-del"></span>
                  </div>
                  <div
                    v-for="(row, i) in qtyAllRows"
                    :key="i"
                    class="replacement-row"
                  >
                    <div class="rt-field">
                      <DxSelectBox
                        :key="
                          'cur-qty-' + i + '-' + (row.currentQty || 'empty')
                        "
                        :value="row.currentQty"
                        :data-source="allMatchedQtys"
                        :show-clear-button="true"
                        placeholder="Any qty"
                        :disabled="isReadOnly"
                        @value-changed="
                          (e) => {
                            if (e && e.event) {
                              row.currentQty = e.value;
                            }
                          }
                        "
                        :search-enabled="true"
                        @focus-in="
                          (e) => {
                            if (e.event.target.select) e.event.target.select();
                          }
                        "
                      />
                    </div>
                    <div class="rt-field rt-field-qty">
                      <DxNumberBox
                        :value="row.newQty"
                        :show-spin-buttons="false"
                        :disabled="isReadOnly"
                        @value-changed="row.newQty = $event.value"
                        :format="'###,###,##0.0000'"
                        :key="'new-qty-' + i"
                      />
                      <span
                        v-if="validated && rowErrors[i] && rowErrors[i].newQty"
                        class="param-error"
                      >
                        {{ rowErrors[i].newQty }}
                      </span>
                    </div>
                    <DxButton
                      :key="'btn-' + i"
                      v-if="!isReadOnly"
                      icon="trash"
                      type="normal"
                      styling-mode="text"
                      class="rt-del-btn"
                      :disabled="qtyAllRows.length === 1"
                      @click="removeQtyAllRow(i)"
                    />
                  </div>
                  <DxButton
                    v-if="!isReadOnly"
                    text="+ Add Row"
                    type="normal"
                    styling-mode="outlined"
                    class="add-row-btn"
                    @click="addQtyAllRow"
                  />
                </template>

                <!-- UpdateQtyNominated -->
                <template v-if="op.value === 'UpdateQtyNominated'">
                  <div
                    v-if="qtyNominatedRows.length > 0"
                    class="replacement-header"
                  >
                    <span class="rt-col-code"
                      >Material Code <span class="required-mark">*</span></span
                    >
                    <span class="rt-col-qty"
                      >Current Qty
                      <span class="qty-optional"
                        >(optional — exact match)</span
                      ></span
                    >
                    <span class="rt-col-qty"
                      >New Qty <span class="required-mark">*</span></span
                    >
                    <span class="rt-col-del"></span>
                  </div>
                  <div
                    v-for="(row, i) in qtyNominatedRows"
                    :key="i"
                    class="replacement-row"
                  >
                    <div class="rt-field">
                      <DxSelectBox
                        :key="
                          'mat-code-' + i + '-' + (row.materialCode || 'empty')
                        "
                        :value="row.materialCode"
                        :data-source="matchedMaterialsList"
                        display-expr="Code"
                        value-expr="Code"
                        :search-enabled="true"
                        :search-expr="['Code', 'Description1']"
                        :drop-down-options="{ width: 300 }"
                        placeholder="Material code..."
                        :show-clear-button="true"
                        :disabled="isReadOnly"
                        @value-changed="onNominatedMaterialChange(row, $event)"
                        item-template="material-item"
                      >
                        <template #material-item="{ data }">
                          <div class="custom-auto-complete">
                            <span class="auto-complete-primary">{{
                              data.Code
                            }}</span>
                            <span class="auto-complete-secondary">{{
                              data.Description1
                            }}</span>
                          </div>
                        </template>
                      </DxSelectBox>
                      <span
                        v-if="
                          validated && rowErrors[i] && rowErrors[i].materialCode
                        "
                        class="param-error"
                      >
                        {{ rowErrors[i].materialCode }}
                      </span>
                      <span v-if="clearedWarnings[i]" class="param-warn">
                        ⚠ Previously selected material was not in the current search results and has been removed.
                      </span>
                    </div>
                    <div class="rt-field rt-field-qty">
                      <DxSelectBox
                        :key="
                          'mat-cur-qty-' + i + '-' + (row.currentQty || 'empty')
                        "
                        :value="row.currentQty"
                        :data-source="getNominatedQtys(row.materialCode)"
                        :show-clear-button="true"
                        placeholder="Any qty"
                        :disabled="isReadOnly"
                        @value-changed="row.currentQty = $event.value"
                        :search-enabled="true"
                      />
                    </div>
                    <div class="rt-field rt-field-qty">
                      <DxNumberBox
                        :key="'mat-new-qty-' + i"
                        :value="row.newQty"
                        :show-spin-buttons="false"
                        :disabled="isReadOnly"
                        @value-changed="row.newQty = $event.value"
                        :format="'###,###,##0.0000'"
                      />
                      <span
                        v-if="validated && rowErrors[i] && rowErrors[i].newQty"
                        class="param-error"
                      >
                        {{ rowErrors[i].newQty }}
                      </span>
                    </div>
                    <DxButton
                      :key="'btn-2-' + i"
                      v-if="!isReadOnly"
                      icon="trash"
                      type="normal"
                      styling-mode="text"
                      class="rt-del-btn"
                      :disabled="qtyNominatedRows.length === 1"
                      @click="removeQtyNominatedRow(i)"
                    />
                  </div>
                  <DxButton
                    v-if="!isReadOnly"
                    text="+ Add Row"
                    type="normal"
                    styling-mode="outlined"
                    class="add-row-btn"
                    @click="addQtyNominatedRow"
                  />
                </template>

                <!-- ReplaceMaterial / ReplaceMaterialAndQty -->
                <template
                  v-if="
                    op.value === 'ReplaceMaterial' ||
                    op.value === 'ReplaceMaterialAndQty'
                  "
                >
                  <div
                    v-if="replacements.length > 0"
                    class="replacement-header"
                  >
                    <span class="rt-col-code">Old Code</span>
                    <span class="rt-col-code">New Code</span>
                    <span
                      v-if="op.value === 'ReplaceMaterialAndQty'"
                      class="rt-col-qty"
                      >New Qty</span
                    >
                    <span class="rt-col-del"></span>
                  </div>
                  <div
                    v-for="(row, i) in replacements"
                    :key="i"
                    class="replacement-row"
                  >
                    <div class="rt-field">
                      <DxSelectBox
                        :value="row.OldCode"
                        :data-source="getOldCodeDataSource(i)"
                        display-expr="Code"
                        value-expr="Code"
                        :search-enabled="true"
                        :search-expr="['Code', 'Description1']"
                        :drop-down-options="{ width: 300 }"
                        placeholder="Old code..."
                        :show-clear-button="true"
                        :disabled="isReadOnly"
                        @value-changed="onOldCodeChange($event, row)"
                        item-template="material-item"
                        :key="'old-' + i + '-' + (row.OldCode || 'empty')"
                      >
                        <template #material-item="{ data }">
                          <div class="custom-auto-complete">
                            <span class="auto-complete-primary">{{
                              data.Code
                            }}</span>
                            <span class="auto-complete-secondary">{{
                              data.Description1
                            }}</span>
                          </div>
                        </template>
                      </DxSelectBox>
                      <span v-if="validated && !row.OldCode" class="param-error"
                        >Old code is required.</span
                      >
                      <span
                        v-if="row.OldCode && getMaterialRef(row.OldCode)"
                        class="ref-hint"
                      >
                        Current qty:
                        {{ getFormattedQty(getMaterialRef(row.OldCode).qty) }}
                        <template v-if="getMaterialRef(row.OldCode).uom">{{
                          getMaterialRef(row.OldCode).uom
                        }}</template>
                      </span>
                    </div>
                    <div class="rt-field">
                      <DxSelectBox
                        :value="row.NewCode"
                        :data-source="getNewCodeDataSource(row.OldCode)"
                        display-expr="Code"
                        value-expr="Code"
                        :search-enabled="true"
                        :search-expr="['Code', 'Description1']"
                        :drop-down-options="{ width: 300 }"
                        placeholder="New code..."
                        :show-clear-button="true"
                        :disabled="isReadOnly"
                        @value-changed="row.NewCode = $event.value"
                        item-template="material-item"
                        :key="'new-' + i + '-' + (row.NewCode || 'empty')"
                      >
                        <template #material-item="{ data }">
                          <div class="custom-auto-complete">
                            <span class="auto-complete-primary">{{
                              data.Code
                            }}</span>
                            <span class="auto-complete-secondary">{{
                              data.Description1
                            }}</span>
                          </div>
                        </template>
                      </DxSelectBox>
                      <span v-if="validated && !row.NewCode" class="param-error"
                        >New code is required.</span
                      >
                    </div>
                    <div
                      v-if="op.value === 'ReplaceMaterialAndQty'"
                      class="rt-field rt-field-qty"
                    >
                      <DxNumberBox
                        :value="row.NewQty"
                        :show-spin-buttons="false"
                        :disabled="isReadOnly"
                        @value-changed="row.NewQty = $event.value"
                        :format="'###,###,##0.0000'"
                      />
                      <span
                        v-if="validated && row.NewQty == null"
                        class="param-error"
                        >Required.</span
                      >
                    </div>
                    <DxButton
                      v-if="!isReadOnly"
                      icon="trash"
                      type="normal"
                      styling-mode="text"
                      class="rt-del-btn"
                      @click="removeReplacementRow(i)"
                    />
                  </div>
                  <div
                    v-if="validated && replacements.length === 0"
                    class="param-error"
                  >
                    At least one replacement row is required.
                  </div>
                  <DxButton
                    v-if="!isReadOnly"
                    text="+ Add Row"
                    type="normal"
                    styling-mode="outlined"
                    class="add-row-btn"
                    @click="addReplacementRow"
                  />
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DxButton from "devextreme-vue/button";
import DxSelectBox from "devextreme-vue/select-box";
import { DxNumberBox } from "devextreme-vue/number-box";
import ApiService from "@/_services/api/api.service";
import { custom as dxCustomDialog } from "devextreme/ui/dialog";
import { formatNumber } from "devextreme/localization";

export default {
  name: "GcBomBulkUpdateMaterialConfig",
  components: { DxButton, DxSelectBox, DxNumberBox },
  props: {
    value: { type: Object, default: null },
    matchedBomList: { type: Array, default: () => [] },
    isReadOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      materialCodeDataSource: null,
      selectedOperation: null,
      previousOperation: null,
      qtyAllRows: [{ currentQty: null, newQty: null }],
      qtyNominatedRows: [
        { materialCode: null, currentQty: null, newQty: null },
      ],
      replacements: [],
      validated: false,
      opListKey: 0,
      clearedWarnings: [],
      materialOperations: [
        {
          value: "UpdateQtyAll",
          label: "Update Qty — all matched materials",
          description:
            "Sets a new quantity on every material line matched by the search criteria across all included BOMs.",
        },
        {
          value: "UpdateQtyNominated",
          label: "Update Qty — nominated material code",
          description: "Sets a new quantity for a specific material code only.",
        },
        {
          value: "ReplaceMaterial",
          label: "Replace material",
          description:
            "Replaces one or more material codes. Existing quantities are preserved.",
        },
        {
          value: "ReplaceMaterialAndQty",
          label: "Replace material and update qty",
          description:
            "Replaces one or more material codes and sets a new quantity for each replacement line.",
        },
      ],
    };
  },
  computed: {
    hasExistingConfig() {
      return (
        this.qtyAllRows.some((r) => r.newQty != null) ||
        this.qtyNominatedRows.some(
          (r) => r.materialCode != null || r.newQty != null,
        ) ||
        this.replacements.length > 0
      );
    },
    isValid() {
      return this.selectedOperation != null && this.buildErrors().length === 0;
    },
    quantityRange() {
      if (!this.matchedBomList || !this.matchedBomList.length) return null;
      let min = Infinity;
      let max = -Infinity;
      let sum = 0;
      let count = 0;
      let uom = null;
      for (const bom of this.matchedBomList) {
        const mats = bom.MatchedMaterials || bom.matchedMaterials;
        if (!mats) continue;
        for (const m of mats) {
          const qty = m.Qty != null ? m.Qty : m.qty;
          if (qty != null) {
            min = Math.min(min, qty);
            max = Math.max(max, qty);
            sum += qty;
            count++;
          }
          if (!uom) uom = m.UOM || m.Uom || m.uom || null;
        }
      }
      if (!count) return null;
      return {
        min,
        max,
        avg: Math.round((sum / count) * 100) / 100,
        uom,
      };
    },
    allMatchedQtys() {
      if (!this.matchedBomList) return [];
      const seen = {};
      const qtys = [];
      for (const bom of this.matchedBomList) {
        const mats = bom.MatchedMaterials || bom.matchedMaterials || [];
        for (const m of mats) {
          const qty = m.Qty != null ? m.Qty : m.qty;
          if (qty != null && !seen[qty]) {
            seen[qty] = true;
            qtys.push(qty);
          }
        }
      }
      return qtys.sort((a, b) => a - b);
    },
    rowErrors() {
      const op = this.selectedOperation;
      const result = {};
      if (op === "UpdateQtyAll") {
        this.qtyAllRows.forEach((row, i) => {
          result[i] = {};
          if (row.newQty == null) result[i].newQty = "New Qty is required.";
        });
      }
      if (op === "UpdateQtyNominated") {
        this.qtyNominatedRows.forEach((row, i) => {
          result[i] = {};
          if (!row.materialCode)
            result[i].materialCode = "Material Code is required.";
          if (row.newQty == null) result[i].newQty = "New Qty is required.";
        });
      }
      return result;
    },
    matchedMaterialsList() {
      if (!this.matchedBomList || !this.matchedBomList.length) return [];
      const seen = {};
      const result = [];
      for (const bom of this.matchedBomList) {
        const mats = bom.MatchedMaterials || bom.matchedMaterials || [];
        for (const m of mats) {
          const code = m.Code || m.MaterialCode || m.code;
          if (code && !seen[code]) {
            seen[code] = true;
            result.push({
              Code: code,
              Description1: m.Description1 || m.Description || m.description || '',
            });
          }
        }
      }
      return result.sort(function(a, b) { return a.Code.localeCompare(b.Code); });
    },
  },
  watch: {
    qtyAllRows: {
      deep: true,
      handler() {
        this.emitChange();
      },
    },
    qtyNominatedRows: {
      deep: true,
      handler() {
        this.emitChange();
      },
    },
    replacements: {
      deep: true,
      handler() {
        this.emitChange();
      },
    },
    selectedOperation() {
      this.emitChange();
    },
    value(newVal) {
      const incoming = JSON.stringify(newVal);
      const current = JSON.stringify(this.buildCurrentConfig());
      if (incoming !== current) {
        this.restoreFromValue(newVal);
      }
    },
    matchedBomList() {
      const validCodes = this.buildMatchedCodeSet();
      const self = this;
      this.qtyNominatedRows.forEach(function(row, i) {
        if (row.materialCode && !validCodes[row.materialCode]) {
          row.materialCode = null;
          row.currentQty = null;
          self.$set(self.clearedWarnings, i, true);
        }
      });
    },
  },
  created() {
    this._oldCodeDsCache = new Map();
    this._newCodeDsCache = new Map();
    if (this.value) {
      this.restoreFromValue(this.value);
    }
  },
  methods: {
    getFormattedQty(value) {
      return formatNumber(value, "##,###,###,###,##0.0000");
    },
    getMaterialRef(code) {
      if (!code || !this.matchedBomList) return null;
      for (const bom of this.matchedBomList) {
        const mats = bom.MatchedMaterials || bom.matchedMaterials;
        if (!mats) continue;
        const found = mats.find(
          (m) => (m.Code || m.MaterialCode || m.MaterialCode) === code,
        );
        if (found != null) {
          return {
            qty: found.Qty != null ? found.Qty : found.qty,
            uom: found.UOM || found.Uom || found.uom || null,
          };
        }
      }
      return null;
    },
    restoreFromValue(val) {
      if (!val) {
        this.selectedOperation = null;
        this.previousOperation = null;
        this.clearParams();
        return;
      }
      this.selectedOperation = val.Operation || null;
      this.previousOperation = val.Operation || null;
      if (val.Operation === "UpdateQtyAll") {
        this.qtyAllRows =
          val.Params && val.Params.QtyUpdates && val.Params.QtyUpdates.length
            ? val.Params.QtyUpdates.map((u) => ({
                currentQty: u.CurrentQty !== undefined ? u.CurrentQty : null,
                newQty: u.NewQty !== undefined ? u.NewQty : null,
              }))
            : [{ currentQty: null, newQty: null }];
        this.clearedWarnings = [];
      } else if (val.Operation === "UpdateQtyNominated") {
        this.qtyNominatedRows =
          val.Params && val.Params.QtyUpdates && val.Params.QtyUpdates.length
            ? val.Params.QtyUpdates.map((u) => ({
                materialCode: u.MaterialCode || null,
                currentQty: u.CurrentQty !== undefined ? u.CurrentQty : null,
                newQty: u.NewQty !== undefined ? u.NewQty : null,
              }))
            : [{ materialCode: null, currentQty: null, newQty: null }];
        this.clearedWarnings = new Array(this.qtyNominatedRows.length).fill(false);
        var validCodes = this.buildMatchedCodeSet();
        var self = this;
        this.qtyNominatedRows.forEach(function(row, i) {
          if (row.materialCode && !validCodes[row.materialCode]) {
            row.materialCode = null;
            row.currentQty = null;
            self.$set(self.clearedWarnings, i, true);
          }
        });
      } else if (
        val.Operation === "ReplaceMaterial" ||
        val.Operation === "ReplaceMaterialAndQty"
      ) {
        this.replacements =
          val.Params && val.Params.Replacements
            ? val.Params.Replacements.map((r) => Object.assign({}, r))
            : [];
        this.clearedWarnings = [];
      }
      this.validated = true;
    },
    async onOperationChange(newOp) {
      if (newOp === this.selectedOperation) return;
      if (this.hasExistingConfig) {
        const confirmed = await dxCustomDialog({
          title: "Change Operation",
          messageHtml:
            "Switching operation will clear your current configuration. Do you want to continue?",
          buttons: [
            { text: "Keep Current", onClick: () => false },
            {
              text: "Continue",
              type: "default",
              stylingMode: "contained",
              onClick: () => true,
            },
          ],
        }).show();
        if (!confirmed) {
          this.opListKey++;
          return;
        }
        this.clearParams();
      }
      this.previousOperation = this.selectedOperation;
      this.selectedOperation = newOp;
    },
    clearParams() {
      this.qtyAllRows = [{ currentQty: null, newQty: null }];
      this.qtyNominatedRows = [
        { materialCode: null, currentQty: null, newQty: null },
      ];
      this.replacements = [];
      this.clearedWarnings = [];
    },
    getDefaultMaterialCodeDataSource() {
      if (!this._defaultMaterialCodeDs) {
        this._defaultMaterialCodeDs = ApiService.createDataSource(
          "api/Product",
          {
            select: ["Code", "Description1", "Inactive", "Status"],
            filter: [["Inactive", "=", "N"], "and", ["Status", "=", "A"]],
          },
        );
      }
      return this._defaultMaterialCodeDs;
    },
    getOldCodeDataSource(rowIndex) {
      if (!this._oldCodeDsCache.has(rowIndex)) {
        this._oldCodeDsCache.set(
          rowIndex,
          ApiService.createDataSource("api/Product", {
            select: ["Code", "Description1", "Inactive", "Status"],
            filter: [["Inactive", "=", "N"], "and", ["Status", "=", "A"]],
          }),
        );
      }
      return this._oldCodeDsCache.get(rowIndex);
    },
    getNewCodeDataSource(OldCode) {
      if (!OldCode) {
        return ApiService.createDataSource("api/Product", {
          select: ["Code", "Description1", "Inactive", "Status"],
          filter: [["Inactive", "=", "N"], "and", ["Status", "=", "A"]],
        });
      }
      if (!this._newCodeDsCache.has(OldCode)) {
        this._newCodeDsCache.set(
          OldCode,
          ApiService.createDataSource("api/Product", {
            select: ["Code", "Description1", "Inactive", "Status"],
            filter: [
              ["Code", "<>", OldCode],
              "and",
              ["Inactive", "=", "N"],
              "and",
              ["Status", "=", "A"],
            ],
          }),
        );
      }
      return this._newCodeDsCache.get(OldCode);
    },
    buildMatchedCodeSet() {
      const set = {};
      if (!this.matchedBomList) return set;
      for (const bom of this.matchedBomList) {
        const mats = bom.MatchedMaterials || bom.matchedMaterials || [];
        for (const m of mats) {
          const code = m.Code || m.MaterialCode || m.code;
          if (code) set[code] = true;
        }
      }
      return set;
    },
    getNominatedQtys(materialCode) {
      if (!materialCode) return [];
      const seen = {};
      const qtys = [];
      for (const bom of this.matchedBomList) {
        const mats = bom.MatchedMaterials || bom.matchedMaterials || [];
        for (const m of mats) {
          const code = m.Code || m.MaterialCode || m.code;
          if (code !== materialCode) continue;
          const qty = m.Qty != null ? m.Qty : m.qty;
          if (qty != null && !seen[qty]) {
            seen[qty] = true;
            qtys.push(qty);
          }
        }
      }
      return qtys.sort((a, b) => a - b);
    },
    addQtyAllRow() {
      this.qtyAllRows.push({ currentQty: null, newQty: null });
    },
    removeQtyAllRow(index) {
      if (this.qtyAllRows.length > 1) {
        this.qtyAllRows.splice(index, 1);
      }
    },
    addQtyNominatedRow() {
      this.qtyNominatedRows.push({
        materialCode: null,
        currentQty: null,
        newQty: null,
      });
      this.clearedWarnings.push(false);
    },
    removeQtyNominatedRow(index) {
      if (this.qtyNominatedRows.length > 1) {
        this.qtyNominatedRows.splice(index, 1);
        this.clearedWarnings.splice(index, 1);
      }
    },
    onNominatedMaterialChange(row, e) {
      if (!e || !e.event) return;
      var i = this.qtyNominatedRows.indexOf(row);
      row.materialCode = e.value;
      row.currentQty = null;
      if (i >= 0) {
        this.$set(this.clearedWarnings, i, false);
      }
    },
    onOldCodeChange(e, row) {
      if (!e || !e.event) return;
      if (row.OldCode !== e.value) {
        row.OldCode = e.value;
        row.NewCode = null;
      }
    },
    addReplacementRow() {
      this.replacements.push({
        OldCode: null,
        NewCode: null,
        NewQty: null,
      });
    },
    removeReplacementRow(index) {
      this.replacements.splice(index, 1);
    },
    buildErrors() {
      const op = this.selectedOperation;
      const errors = [];
      if (!op) {
        errors.push("Please select a material update operation.");
        return errors;
      }
      if (op === "UpdateQtyAll") {
        if (this.qtyAllRows.length === 0) {
          errors.push("At least one row is required.");
        } else {
          this.qtyAllRows.forEach((row, i) => {
            if (row.newQty == null || row.newQty === "") {
              errors.push(`Row ${i + 1}: New quantity is required.`);
            }
          });
        }
      }
      if (op === "UpdateQtyNominated") {
        if (this.qtyNominatedRows.length === 0) {
          errors.push("At least one row is required.");
        } else {
          this.qtyNominatedRows.forEach((row, i) => {
            if (!row.materialCode) {
              errors.push(`Row ${i + 1}: Material code is required.`);
            }
            if (row.newQty == null || row.newQty === "") {
              errors.push(`Row ${i + 1}: New quantity is required.`);
            }
          });
        }
      }
      if (op === "ReplaceMaterial" || op === "ReplaceMaterialAndQty") {
        if (this.replacements.length === 0) {
          errors.push("At least one replacement row is required.");
        }
        this.replacements.forEach((r, i) => {
          if (!r.OldCode) errors.push(`Row ${i + 1}: Old code is required.`);
          if (!r.NewCode) errors.push(`Row ${i + 1}: New code is required.`);
          if (op === "ReplaceMaterialAndQty" && r.NewQty == null) {
            errors.push(`Row ${i + 1}: New quantity is required.`);
          }
        });
      }
      return errors;
    },
    buildCurrentConfig() {
      const op = this.selectedOperation;
      let params = {};
      if (op === "UpdateQtyAll") {
        params.QtyUpdates = this.qtyAllRows.map((row) => {
          const item = { NewQty: row.newQty };
          if (row.currentQty !== null && row.currentQty !== "") {
            item.CurrentQty = row.currentQty;
          }
          return item;
        });
      } else if (op === "UpdateQtyNominated") {
        params.QtyUpdates = this.qtyNominatedRows.map((row) => {
          const item = { MaterialCode: row.materialCode, NewQty: row.newQty };
          if (row.currentQty !== null && row.currentQty !== "") {
            item.CurrentQty = row.currentQty;
          }
          return item;
        });
      } else if (op === "ReplaceMaterial") {
        params.Replacements = this.replacements.map((r) =>
          Object.assign({}, r),
        );
      } else if (op === "ReplaceMaterialAndQty") {
        params.Replacements = this.replacements.map((r) =>
          Object.assign({}, r),
        );
      }
      return {
        Operation: op,
        Params: params,
      };
    },
    validate() {
      this.validated = true;
      return this.isValid;
    },
    emitChange() {
      const config = this.buildCurrentConfig();
      this.$emit("config-change", config);
      this.$emit("validation-change", { isValid: this.isValid });
    },
  },
};
</script>

<style lang="scss">
.gc-bom-bulk-update-material-config {
  .config-card {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    // margin-bottom: 16px;
  }

  .config-card-header {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #dee2e6;
    // background: #f8f9fa;
    border-radius: 6px 6px 0 0;

    background: #e0f2fe;
    border-color: #b9e5fb;
    justify-content: space-between;

    .config-card-title-secondary {
      font-size: 12px;
      color: #0369a1;
    }
  }

  .config-card-title {
    font-size: 13px;
    font-weight: 600;
    color: #344054;
  }

  .config-card-body {
    padding: 16px;
  }

  .param-error-top {
    display: block;
    margin-bottom: 10px;
  }

  // Operation section
  .operation-section {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .operation-section-header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding-bottom: 8px;
    border-bottom: 1px solid #dee2e6;

    > .dx-button .dx-button-content {
      display: flex;
      align-items: center;
    }
  }

  .operation-section-title {
    font-weight: 500;
    font-size: 13px;
    color: #344054;
  }

  .operation-clear-btn {
    font-size: 12px;
    color: #6c757d;
    padding: 2px 6px;
    transition: color 0.15s;

    &:hover {
      color: #dc3545;
    }

    &:disabled {
      color: #dee2e6;
      cursor: not-allowed;
    }
  }

  // Operation radio list
  .operation-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .operation-item {
    display: flex;
    flex-direction: column;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    transition: border-color 0.15s, background 0.15s;
    overflow: hidden;

    &:hover {
      border-color: #03a9f4;
    }

    &.op-active {
      border-color: #03a9f4;
      background: #e8f6fe;
    }
  }

  .op-header {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    cursor: pointer;

    .op-radio {
      margin-top: 2px;
      flex-shrink: 0;
      accent-color: #03a9f4;
    }

    .op-text {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .op-label {
      font-size: 13px;
      font-weight: 500;
      color: #101828;
    }

    .op-desc {
      font-size: 12px;
      color: #6c757d;
    }
  }

  // Inline params — expands inside the active item
  .op-params-inline {
    border-top: 1px solid #b3e5fc;
    padding: 14px 12px 14px 34px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: #f0faff;
  }

  .param-row {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-width: 480px;
  }

  .param-label {
    font-size: 12px;
    font-weight: 500;
    color: #344054;
  }

  .required-mark {
    color: #dc3545;
    margin-left: 2px;
  }

  .qty-optional {
    font-size: 10px;
    color: #6c757d;
    // margin-left: 4px;
    font-weight: 400;
  }

  .param-input-wrap {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .ref-hint {
    font-size: 11px;
    color: #6c757d;
    font-style: italic;
  }

  // UpdateQtyAll — range context
  .qty-range-context {
    margin-bottom: 12px;
  }

  .qty-range-label {
    display: block;
    font-size: 10px;
    font-weight: 700;
    color: #6c757d;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    margin-bottom: 8px;
  }

  .qty-range-stats {
    display: flex;
    gap: 8px;
  }

  .qty-stat-box {
    display: flex;
    flex-direction: row;
    gap: 2px;
    padding: 8px 14px;
    border: 1px solid #dee2e6;
    border-radius: 6px;
    background: #f8f9fa;
    min-width: 80px;
    align-items: center;
  }

  .qty-stat-key {
    font-size: 10px;
    font-weight: 600;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  .qty-stat-val {
    font-size: 12px;
    font-weight: 600;
    color: #101828;
  }

  .qty-stat-uom {
    font-size: 11px;
    font-weight: 400;
    color: #6c757d;
  }

  // Inline row (qty + Unit side-by-side)
  .param-row-inline {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    max-width: 480px;
  }

  .param-col {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;

    &.param-col-wide {
      flex: 2;
    }
    &.param-col-narrow {
      flex: 1;
      min-width: 120px;
      max-width: 180px;
    }
  }

  .param-error {
    display: block;
    font-size: 12px;
    color: #dc3545;
    margin-top: 2px;
  }

  .param-warn {
    display: block;
    font-size: 12px;
    color: #856404;
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 4px;
    padding: 4px 8px;
    margin-top: 2px;
  }

  // Replacement table
  .replacement-header {
    display: flex;
    gap: 8px;
    align-items: center;
    margin-bottom: 4px;

    .rt-col-code {
      // flex: 1;
      width: 250px;
      font-size: 11px;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.4px;
    }

    .rt-col-qty {
      width: 125px;
      font-size: 11px;
      font-weight: 600;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.4px;
    }

    .rt-col-del {
      width: 28px;
    }
  }

  .replacement-row {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    margin-bottom: 6px;

    .rt-del-btn {
      flex-shrink: 0;
      margin-top: 6px;
      color: #6c757d;

      &:hover {
        color: #dc3545;
      }
    }
  }

  .rt-field {
    // flex: 1;
    width: 250px;
    display: flex;
    flex-direction: column;
    gap: 2px;

    .dx-selectbox,
    .dx-numberbox {
      width: 100%;
    }

    &.rt-field-qty {
      flex: 0 0 130px;
    }
  }

  .add-row-btn {
    margin-top: 4px;
    font-size: 13px;
  }
}
</style>
